// PPT Maker Agent via Supabase Edge Function (SlidesAi)
// Generates complete standalone HTML slides with Tailwind CSS
import { supabase } from "@/integrations/supabase/client";

export interface PPTPayload {
  title: string;
  totalSlides: number;
  slides: string[]; // Array of complete HTML documents (one per slide)
  assets?: string[];
}

// Verify image URL is accessible using browser Image()
async function verifyImageUrl(url: string): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    try {
      const img = new Image();
      const timer = setTimeout(() => {
        img.src = "";
        resolve(false);
      }, 7000);
      img.onload = () => {
        clearTimeout(timer);
        resolve(true);
      };
      img.onerror = () => {
        clearTimeout(timer);
        resolve(false);
      };
      img.src = url;
    } catch {
      resolve(false);
    }
  });
}

// Resolve Unsplash access key from environment
function getUnsplashKey(): string | undefined {
  try {
    const vite = (import.meta as any)?.env?.VITE_UNSPLASH_ACCESS_KEY;
    if (vite) return String(vite);
  } catch { }
  try {
    const node = (globalThis as any)?.process?.env?.UNSPLASH_ACCESS_KEY;
    if (node) return String(node);
  } catch { }
  return undefined;
}

// Search Unsplash for images, return up to 2 for fallback
async function searchUnsplashImages(query: string): Promise<string[]> {
  const key = getUnsplashKey();
  if (!key) return [];

  const url = new URL("https://api.unsplash.com/search/photos");
  url.searchParams.set("query", query);
  url.searchParams.set("per_page", "5");
  url.searchParams.set("order_by", "relevant");
  url.searchParams.set("orientation", "landscape");

  try {
    const res = await fetch(url.toString(), {
      headers: { Authorization: `Client-ID ${key}` },
    });
    if (!res.ok) return [];
    const data = await res.json();
    const results = Array.isArray(data?.results) ? data.results : [];

    const validUrls: string[] = [];
    for (const r of results) {
      if (validUrls.length >= 2) break;
      const candidate: string | undefined =
        r?.urls?.regular || r?.urls?.full || r?.urls?.small;
      if (!candidate || !/^https?:\/\//i.test(candidate)) continue;
      const isValid = await verifyImageUrl(candidate);
      if (isValid) validUrls.push(candidate);
    }
    return validUrls;
  } catch {
    return [];
  }
}

// Simple cache for Unsplash queries
const unsplashCache = new Map<string, string[]>();

async function cachedUnsplash(query: string): Promise<string[]> {
  const key = query.toLowerCase().trim();
  if (unsplashCache.has(key)) return unsplashCache.get(key)!;
  const urls = await searchUnsplashImages(key);
  unsplashCache.set(key, urls);
  return urls;
}

// Extract the first balanced JSON object string from text
function extractBalancedJsonString(text: string): string | null {
  let depth = 0;
  let inStr = false;
  let esc = false;
  let start = -1;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inStr) {
      if (esc) {
        esc = false;
      } else if (ch === "\\") {
        esc = true;
      } else if (ch === '"') {
        inStr = false;
      }
      continue;
    }
    if (ch === '"') {
      inStr = true;
      continue;
    }
    if (ch === "{") {
      if (depth === 0) start = i;
      depth++;
    } else if (ch === "}") {
      depth--;
      if (depth === 0 && start !== -1) {
        return text.slice(start, i + 1);
      }
    }
  }
  return null;
}

const SYSTEM_PROMPT = `You are a professional presentation designer that creates modern, clean, visually structured slides.

PROCESS
Research the topic accurately
Plan a logical slide structure
Generate each slide as a complete standalone HTML document

OUTPUT FORMAT

Return STRICT JSON only (no prose, no markdown):

{
  "title": "Presentation Title",
  "totalSlides": number,
  "slides": [
    "<!DOCTYPE html>...</html>",
    "<!DOCTYPE html>...</html>"
  ]
}

IMPORTANT: The HTML in each slide MUST be properly formatted with indentation (2 spaces). Do NOT output minified/single-line HTML.

SLIDE TYPES
1. COVER SLIDE (Slide 1 — REQUIRED)

Full-screen background image with dark overlay
Centered title + subtitle
Bold or gradient headline

2. CONTENT SLIDE (Default)

Header bar with title (NO decorative dots)
Two-column layout — ALWAYS flex-row
Left: text (55–60%)
Right: image (40–45%)
Compact padding (p-3 or p-4)

1 main idea per slide

3. TEXT-ONLY SLIDE (Optional)
Same header
Full-width structured content
Used for comparisons or dense info
Can include data tables or charts when needed

4. CLOSING SLIDE (Last slide — REQUIRED)
Clean, centered "Thank You" or conclusion or according to the situation
Optional call-to-action
BASE HTML (REQUIRED FOR EVERY SLIDE)
Full HTML document (<!DOCTYPE html>)
Tailwind via CDN
Inter font
Material Icons
Fixed slide size: 1280x720
.ppt-slide container fills height

IMAGE RULES (CRITICAL - USE THESE EXACT URLs)

Pick from this list based on topic. COPY URL EXACTLY:

- Technology/AI: https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=800&q=80
- Computers: https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=800&q=80
- Business/Office: https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&q=80
- Teamwork: https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=80
- Nature/Forest: https://images.unsplash.com/photo-1448375240586-882707db888b?w=800&q=80
- Ocean/Water: https://images.unsplash.com/photo-1505142468610-359e7d316be0?w=800&q=80
- Mountains: https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=800&q=80
- Health/Medical: https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=800&q=80
- Education: https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=800&q=80
- Finance/Money: https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&q=80
- City/Urban: https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?w=800&q=80
- Food: https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=800&q=80
- Travel: https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=800&q=80
- Science: https://images.unsplash.com/photo-1532094349884-543bc11b234d?w=800&q=80
- Space: https://images.unsplash.com/photo-1446776811953-b23d57bd21aa?w=800&q=80
- Abstract/Pattern: https://images.unsplash.com/photo-1557683316-973673baf926?w=800&q=80
- Gradient: https://images.unsplash.com/photo-1579546929518-9e396f3cc809?w=800&q=80
- Dark Background: https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&q=80

Image HTML format:
<img src="URL" alt="description" class="w-full h-full object-cover rounded-lg" loading="lazy" />

CHART/TABLE CAPABILITY
When data comparison or statistics are needed, you can create:
- Simple data tables using Tailwind classes
- Bar charts using colored divs with percentages
- Comparison grids

STYLING RULES
- Paragraph text (<p>) must use: class="text-xl" (20px font size)
- Lists should use text-lg or text-xl
- Keep text readable and well-spaced

CRITICAL RULES (NON-NEGOTIABLE):
Max 12 slides
Slide 1 = Cover
Last slide = Closing
Content slides = flex-row ONLY
Text LEFT, Image RIGHT
Subtle animation can be accepted
NO decorative dots in headers
Compact spacing only
60-100 words per slide max
Use Material Icons sparingly
Consistent color palette (2-3 accents)
Properly indented HTML (2 spaces per level)

Return JSON ONLY`;
function buildUserPrompt(topic: string): string {
  return `
Create a professional presentation about: "${topic}"

Requirements:
- 6-10 slides depending on content depth
- Accurate, factual, well-researched information
- Slide 1: Cover with background image and centered title
- Content slides: Two-column layout (text left, image right)
- Include data tables or comparison charts if relevant to the topic
- Final slide: Clean closing / thank-you slide
- Consistent modern color theme
- Use images from the provided list that match each slide's content
- Properly formatted HTML with indentation
- Paragraph text should be 20px (text-xl class)
`;
}


export async function generatePPTWithGroq(
  userPrompt: string
): Promise<PPTPayload> {
  const { data, error } = await supabase.functions.invoke("runAgent", {
    body: {
      agent: "SlidesAi",
      query: userPrompt,
    },
  });

  if (error) {
    throw new Error(`SlidesAi function failed: ${error.message || String(error)}`);
  }

  const content: string | undefined =
    (data && (data.response || data.content || data.message)) ||
    data?.choices?.[0]?.message?.content;
  if (!content) throw new Error("Empty response from SlidesAi");

  const parsed = await parsePptJson(content);
  return parsed;
}

async function parsePptJson(content: string): Promise<PPTPayload> {
  // Clean up the response
  let cleaned = content
    .trim()
    .replace(/^```json\s*/i, "")
    .replace(/^```\s*/i, "")
    .replace(/```\s*$/i, "")
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, "");

  // Normalize quotes
  cleaned = cleaned
    .replace(/[\u201C\u201D\u201E\u201F\u2033]/g, '"')
    .replace(/[\u2018\u2019\u201A\u201B\u2032]/g, "'");

  // Extract balanced JSON
  const balanced = extractBalancedJsonString(cleaned) || extractBalancedJsonString(content);
  if (balanced) cleaned = balanced;

  // Remove trailing commas
  cleaned = cleaned.replace(/,\s*(?=[}\]])/g, "");

  // Fix common escape sequence issues in JSON strings
  // Replace literal newlines inside strings with \n
  cleaned = cleaned.replace(/"([^"]*?)\n([^"]*?)"/g, (match) => {
    return match.replace(/\n/g, '\\n').replace(/\r/g, '\\r').replace(/\t/g, '\\t');
  });

  let parsed: any;
  try {
    parsed = JSON.parse(cleaned);
  } catch (e: any) {
    // Try more aggressive cleanup
    let cleanedMore = cleaned;

    // Find the slides array and fix escape issues within it
    try {
      // Extract just the outer structure and parse slides separately
      const slidesMatch = cleanedMore.match(/"slides"\s*:\s*\[([\s\S]*?)\]\s*(?:,|\})/); if (slidesMatch) {
        // Replace the problematic slides array with a placeholder
        const slidesContent = slidesMatch[1];
        // Split by slide boundaries (looking for complete HTML docs)
        const slideStrings: string[] = [];
        let depth = 0;
        let start = -1;
        let inString = false;
        let escape = false;

        for (let i = 0; i < slidesContent.length; i++) {
          const ch = slidesContent[i];
          if (escape) {
            escape = false;
            continue;
          }
          if (ch === '\\') {
            escape = true;
            continue;
          }
          if (ch === '"' && !escape) {
            if (!inString) {
              inString = true;
              if (depth === 0) start = i;
            } else {
              inString = false;
              if (depth === 0 && start !== -1) {
                slideStrings.push(slidesContent.slice(start, i + 1));
                start = -1;
              }
            }
          }
        }

        // Try to parse with cleaned slide strings
        if (slideStrings.length > 0) {
          const cleanedSlides = slideStrings.map(s => {
            try {
              return JSON.parse(s);
            } catch {
              // Remove problematic escape sequences
              const fixed = s
                .replace(/\\(?!["\\nrtbfu])/g, '\\\\') // Fix bad escapes
                .replace(/[\x00-\x1F]/g, ' '); // Remove control chars
              try {
                return JSON.parse(fixed);
              } catch {
                return s.slice(1, -1); // Just strip quotes as fallback
              }
            }
          });

          // Try to parse the rest
          const baseJson = cleanedMore
            .replace(/"slides"\s*:\s*\[[\s\S]*?\]/, '"slides": []')
            .replace(/,\s*(?=[}\]])/g, "");

          try {
            parsed = JSON.parse(baseJson);
            parsed.slides = cleanedSlides;
          } catch {
            // Final fallback
            parsed = {
              title: "AI Presentation",
              totalSlides: cleanedSlides.length,
              slides: cleanedSlides,
            };
          }
        }
      }
    } catch {
      // Ignore advanced parsing errors
    }

    // If still not parsed, try original fallback
    if (!parsed) {
      const m = cleaned.match(/\{[\s\S]*\}/);
      if (!m) {
        throw new Error(`Failed to parse PPT JSON: ${e?.message || "invalid JSON"}`);
      }
      const lastTry = m[0].replace(/,\s*(?=[}\]])/g, "");
      try {
        parsed = JSON.parse(lastTry);
      } catch (e2: any) {
        throw new Error(`Failed to parse PPT JSON after cleanup: ${e2?.message || "invalid JSON"}`);
      }
    }
  }

  // Validate and construct payload
  const title = parsed.title || "AI Presentation";
  const slides: string[] = Array.isArray(parsed.slides) ? parsed.slides : [];
  const totalSlides = typeof parsed.totalSlides === "number" ? parsed.totalSlides : slides.length;
  const assets: string[] = Array.isArray(parsed.assets) ? parsed.assets : [];

  if (slides.length === 0) {
    throw new Error("No slides in PPT payload");
  }

  // Sanitize each slide - remove any disallowed script tags but keep Tailwind CDN
  const sanitizedSlides = slides.map((slideHtml: string) => {
    let html = String(slideHtml);

    // Remove script tags except Tailwind CDN
    html = html.replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, (match) => {
      if (match.includes("cdn.tailwindcss.com")) {
        return match;
      }
      return "";
    });

    // Remove inline event handlers
    html = html.replace(/\s(on[a-z]+)=("[^"]*"|'[^']*')/gi, "");

    // Remove javascript: URLs
    html = html.replace(/href\s*=\s*("|')javascript:[\s\S]*?\1/gi, 'href="#"');

    return html;
  });

  return {
    title,
    totalSlides,
    slides: sanitizedSlides,
    assets: assets.filter((u) => typeof u === "string" && /^https?:\/\//i.test(u)),
  };
}

export default generatePPTWithGroq;
